import type { BlockDefinition, StockPillProps } from '../types';
import { escapeHtml } from '../html';

/**
 * Stock / scarcity pill — companion to the countdown's urgency timer.
 * Shows "🔥 Only 7 spots left" with the number decrementing toward
 * `endCount` linearly over `durationSeconds`. Fresh per page visit so
 * every new arrival sees the start count, then watches it tick down.
 *
 * Server renders the static start number; a tiny inline JS snippet
 * (~500 bytes) updates it every second with a pulse animation on each
 * change.
 *
 * Decrements stop at `endCount` — we deliberately don't allow it to
 * hit zero by default (that breaks urgency: a visitor who sees "0 spots
 * left" gives up). End at 1 by default to keep the FOMO live until they
 * click.
 */
export const stockPill: BlockDefinition<StockPillProps> = {
  type: 'stock-pill',
  meta: {
    label: 'Spots left',
    icon: '🔥',
    category: 'content',
    description: 'Scarcity pill that ticks down as visitors arrive.',
  },
  defaults: () => ({
    startCount: 10,
    endCount: 1,
    durationSeconds: 10 * 60, // 10 minutes
    template: '🔥 Only {n} spots left at this price',
    variant: 'warning',
    align: 'center',
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<StockPillProps>;
    let start =
      typeof i.startCount === 'number' && Number.isFinite(i.startCount)
        ? Math.floor(i.startCount)
        : 10;
    if (start < 1) start = 1;
    if (start > 9999) start = 9999;

    let end =
      typeof i.endCount === 'number' && Number.isFinite(i.endCount)
        ? Math.floor(i.endCount)
        : 1;
    if (end < 0) end = 0;
    // End must be strictly less than start, otherwise nothing decrements.
    if (end >= start) end = Math.max(0, start - 1);

    let duration =
      typeof i.durationSeconds === 'number' && Number.isFinite(i.durationSeconds)
        ? Math.floor(i.durationSeconds)
        : 600;
    if (duration < 1) duration = 1;
    if (duration > 7 * 24 * 60 * 60) duration = 7 * 24 * 60 * 60;

    let template = typeof i.template === 'string' ? i.template.slice(0, 240) : '';
    if (!template) template = '🔥 Only {n} spots left at this price';
    // Force the {n} placeholder so the customer's edits still show the number.
    if (!template.includes('{n}')) template = template + ' {n}';

    return {
      startCount: start,
      endCount: end,
      durationSeconds: duration,
      template,
      variant:
        i.variant === 'danger' ? 'danger' : i.variant === 'minimal' ? 'minimal' : 'warning',
      align: ['left', 'center', 'right'].includes(i.align as string)
        ? (i.align as StockPillProps['align'])
        : 'center',
    };
  },
  render(props) {
    const safeId = 'pb_sp_' + Math.random().toString(36).slice(2, 10);
    const justify =
      props.align === 'left' ? 'flex-start' : props.align === 'right' ? 'flex-end' : 'center';
    const initialText = props.template.replace(/\{n\}/g, String(props.startCount));

    // Variant palette — bg + text + ring colors. Picked so each variant
    // pops cleanly on both light and dark theme backgrounds.
    const palette = {
      warning: {
        bg: '#fff3cd', // soft amber
        text: '#7a4900',
        ring: 'rgba(245,158,11,0.35)',
        dotBg: '#f59e0b',
      },
      danger: {
        bg: '#fee2e2',
        text: '#7f1d1d',
        ring: 'rgba(239,68,68,0.35)',
        dotBg: '#ef4444',
      },
      minimal: {
        bg: 'rgba(0,0,0,0.06)',
        text: 'var(--text)',
        ring: 'rgba(0,0,0,0.12)',
        dotBg: 'rgba(0,0,0,0.4)',
      },
    }[props.variant];

    // The pulsing dot leftmost is a tiny scarcity-attention signal —
    // looks like a live indicator. Pure CSS animation, no JS.
    return `<div class="pb-block pb-stock-pill" style="display:flex;justify-content:${justify};">
  <div data-pb-id="${safeId}"
       data-start="${props.startCount}"
       data-end="${props.endCount}"
       data-duration="${props.durationSeconds}"
       data-template="${escapeHtml(props.template)}"
       style="display:inline-flex;align-items:center;gap:10px;padding:8px 16px;border-radius:9999px;background:${palette.bg};color:${palette.text};font-size:14px;font-weight:600;box-shadow:0 0 0 1px ${palette.ring};max-width:100%;">
    <span aria-hidden="true" style="position:relative;display:inline-flex;align-items:center;justify-content:center;width:8px;height:8px;flex-shrink:0;">
      <span style="position:absolute;inset:0;background:${palette.dotBg};border-radius:9999px;animation:pb_pulse 1.6s ease-out infinite;opacity:0.6;"></span>
      <span style="position:relative;width:8px;height:8px;background:${palette.dotBg};border-radius:9999px;"></span>
    </span>
    <span data-text style="display:inline-block;transition:transform 0.18s ease;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(initialText)}</span>
  </div>
</div>
<style>@keyframes pb_pulse{0%{transform:scale(1);opacity:0.6;}80%{transform:scale(2.2);opacity:0;}100%{transform:scale(2.2);opacity:0;}}</style>
<script>(function(){var el=document.querySelector('[data-pb-id="${safeId}"]');if(!el)return;var span=el.querySelector('[data-text]');if(!span)return;var start=parseInt(el.getAttribute('data-start')||'',10);var end=parseInt(el.getAttribute('data-end')||'',10);var dur=parseInt(el.getAttribute('data-duration')||'',10);var tpl=el.getAttribute('data-template')||'';var loaded=Date.now();var last=start;function tick(){var elapsed=(Date.now()-loaded)/1000;var p=Math.min(1,elapsed/dur);var n=Math.max(end,Math.round(start-(start-end)*p));if(n!==last){last=n;span.textContent=tpl.split('{n}').join(String(n));span.style.transform='scale(1.12)';setTimeout(function(){span.style.transform='scale(1)';},180);}if(elapsed>=dur){clearInterval(iv);}}var iv=setInterval(tick,1000);})();</script>`;
  },
};
